import React from "react";
import {
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Tag,
  Typography,
  Button,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { KlearlyAPKConfig } from "../../../../interfaces/klearlyApkConfig.interface";

const { Text } = Typography;

type VersionKey =
  | "fullVersion"
  | "liteVersion"
  | "liteSandboxVersion"
  | "iOSVersion"
  | "liteVersionPax"
  | "liteVersionSoftpay";

const VERSION_LABELS: Record<VersionKey, string> = {
  fullVersion: "Full Version",
  liteVersion: "Lite Version",
  liteSandboxVersion: "Lite Sandbox Version",
  liteVersionPax: "Lite Pax Version",
  liteVersionSoftpay: "Lite Softpay Version",
  iOSVersion: "iOS Version",
};

function getLastUpdatedText(updatedOn?: Date | string) {
  if (!updatedOn) return "Last update time unknown";
  const updated = new Date(updatedOn);
  if (isNaN(updated.getTime())) return "Last update time unknown";
  const now = new Date();
  const diffMs = now.getTime() - updated.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return "Last updated today";
  if (days === 1) return "Last updated 1 day ago";
  return `Last updated ${days} days ago`;
}

export default function ApkVersionsGrid({
  config,
  loading,
  onEdit,
}: {
  config?: KlearlyAPKConfig;
  loading: boolean;
  onEdit: (key: VersionKey) => void;
}) {
  const renderItem = (key: VersionKey) => {
    const v = config?.[key];
    return (
      <Col xs={24} md={12} lg={8} key={key}>
        <Card
          title={VERSION_LABELS[key]}
          loading={loading}
          style={{ marginBottom: 16 }}
          extra={
            <Space size="small" align="center">
              <Text style={{ fontSize: 12 }} type="secondary">
                {getLastUpdatedText(v?.updatedOn)}
              </Text>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(key)}
              />
            </Space>
          }
        >
          {v ? (
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Name">{v.name}</Descriptions.Item>
                <Descriptions.Item label="Build Number">
                  {v.buildNumber}
                </Descriptions.Item>
                <Descriptions.Item label="File URL">
                  <Text
                    style={{ fontSize: 12, width: "130px" }}
                    type="secondary"
                    ellipsis={{ tooltip: true }}
                  >
                    <a
                      href={v.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={v.fileUrl}
                    >
                      {v.fileUrl}
                    </a>
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Public URL">
                  <Text
                    style={{ fontSize: 12, width: "120px" }}
                    type="secondary"
                    ellipsis={{ tooltip: true }}
                  >
                    <a href={v.displayUrl} target="_blank" rel="noreferrer">
                      {v.displayUrl}
                    </a>
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Updated On">
                  {v.updatedOn ? new Date(v.updatedOn).toLocaleString() : "-"}
                </Descriptions.Item>
                {v?.metadata?.rapydCoreVersion && (
                  <Descriptions.Item label="Rapyd Core Version">
                    <Tag color="blue">{v.metadata.rapydCoreVersion}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Space>
          ) : (
            <Text type="secondary">No data</Text>
          )}
        </Card>
      </Col>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      {(Object.keys(VERSION_LABELS) as VersionKey[]).map((k) => renderItem(k))}
    </Row>
  );
}
